// EventsPage.jsx  — complete updated file
import React, { useState, useCallback, useRef, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Paper,
  Autocomplete,
  TextField,
  Button,
  FormControl,
  Select,
  MenuItem,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Pagination,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import FlagIcon from "@mui/icons-material/Flag";
import BrokenImageIcon from "@mui/icons-material/BrokenImage";
import AddIcon from "@mui/icons-material/Add";
import {
  getAlarmFiles,
  getAlarmPage,
  getAlarmTypeList,
  getbaseInfo,
} from "../utility";
import EventDetailModal from "./EventDetailModal";
import SelectDeviceModal from "./SelectDeviceModal";

// ─── Constants ───────────────────────────────────────────────────────────────
const PAGE_SIZE = 12;

const ALARM_TYPE_MAP = {
  forwardCollisionWarning: "Forward Collision",
  laneChangeWarning: "Lane Departure",
  driverFatigue: "Driver Fatigue",
  overspeed: "Overspeed",
  geofence: "Geofence",
  routeDeviation: "Route Deviation",
  tampering: "Tampering",
};
const toDisplayType = (raw) => ALARM_TYPE_MAP[raw] ?? raw ?? "—";
const OPERATE_MAP = { 1: "Completed", 2: "Pending", 3: "Processing" };
const toStatus = (op) => OPERATE_MAP[op] ?? "Completed";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const CAM_LABELS = ["CH1", "CH2", "CH3", "CH4"];

function buildCamSlots(fileList = []) {
  const slots = [null, null, null, null];
  for (const f of fileList) {
    const m = f.fileUrl?.match(/_0(\d)_0[02]_/);
    if (m) {
      const idx = parseInt(m[1], 10);
      if (idx >= 0 && idx <= 3) slots[idx] = f;
    }
  }
  return slots;
}

// ─── Sub-components ───────────────────────────────────────────────────────────
const CamThumb = ({ file, index }) => {
  const [imgErr, setImgErr] = useState(false);

  if (!file) {
    return (
      <Box
        sx={{
          flex: 1,
          aspectRatio: "4/3",
          background: "#e8eaef",
          borderRadius: "4px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "2px",
        }}
      >
        <BrokenImageIcon sx={{ fontSize: 14, color: "#b0bac8" }} />
        <Typography sx={{ fontSize: "8px", color: "#94a3b8" }}>
          {CAM_LABELS[index]}
        </Typography>
      </Box>
    );
  }

  if (file.fileType === "02") {
    return (
      <Box
        sx={{
          flex: 1,
          aspectRatio: "4/3",
          background: "#1a2540",
          borderRadius: "4px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "2px",
        }}
      >
        <PlayCircleIcon sx={{ fontSize: 22, color: "#fff" }} />
        <Typography sx={{ fontSize: "8px", color: "rgba(255,255,255,.65)" }}>
          {CAM_LABELS[index]}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        aspectRatio: "4/3",
        borderRadius: "4px",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#e0e3ea",
        position: "relative",
      }}
    >
      {imgErr ? (
        <BrokenImageIcon sx={{ fontSize: 14, color: "#94a3b8" }} />
      ) : (
        <Box
          component="img"
          src={file.fileUrl}
          alt={CAM_LABELS[index]}
          onError={() => setImgErr(true)}
          sx={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      )}
      <Box
        sx={{
          position: "absolute",
          bottom: 3,
          left: 4,
          fontSize: "8px",
          background: "rgba(0,0,0,0.5)",
          color: "#fff",
          px: "3px",
          py: "1px",
          borderRadius: "3px",
          lineHeight: 1.4,
        }}
      >
        {CAM_LABELS[index]}
      </Box>
    </Box>
  );
};

const HandlingBadge = ({ operate, invalidStatus }) => {
  let label = "Valid",
    bg = "#e6f4ea",
    color = "#166534";
  if (invalidStatus === 1) {
    label = "Invalid";
    bg = "#fce8e6";
    color = "#b91c1c";
  } else if (!operate) {
    label = "Pending";
    bg = "#fef3c7";
    color = "#92400e";
  }
  return (
    <Box
      sx={{
        px: 1.2,
        py: "2px",
        borderRadius: "20px",
        background: bg,
        color,
        fontSize: "11px",
        fontWeight: 600,
        display: "flex",
        alignItems: "center",
        gap: "4px",
      }}
    >
      <FlagIcon sx={{ fontSize: 11 }} />
      {label}
    </Box>
  );
};

const StatusLabel = ({ value }) => {
  const map = {
    Completed: "#16a34a",
    Pending: "#d97706",
    Processing: "#2563eb",
  };
  const c = map[value] ?? "#6b7280";
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
      <Box
        sx={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          background: c,
          flexShrink: 0,
        }}
      />
      <Typography sx={{ fontSize: "11px", fontWeight: 600, color: c }}>
        {value}
      </Typography>
    </Box>
  );
};

// ─── EventCard — now clickable ────────────────────────────────────────────────
const EventCard = ({ record, fileMap, onClick }) => {
  const camSlots = buildCamSlots(fileMap[record.alarmId] ?? []);
  const status = toStatus(record.operate);
  return (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        border: "0.5px solid #e4e7ef",
        borderRadius: "10px",
        p: "14px",
        cursor: "pointer",
        transition: "border-color .15s, box-shadow .15s",
        "&:hover": {
          borderColor: "rgba(8,45,107,0.35)",
          boxShadow: "0 2px 12px rgba(8,45,107,0.09)",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: "4px",
        }}
      >
        <Typography
          sx={{ fontSize: "13px", fontWeight: 600, color: "#1a2540" }}
        >
          {record.deviceName}
        </Typography>
        <HandlingBadge
          operate={record.operate}
          invalidStatus={record.invalidStatus}
        />
      </Box>
      <Typography
        sx={{ fontSize: "14px", fontWeight: 600, color: "#082D6B", mb: "2px" }}
      >
        {toDisplayType(record.alarmType)}
      </Typography>
      <Typography sx={{ fontSize: "12px", color: "#64748b", mb: "10px" }}>
        {record.alarmTime}
      </Typography>
      <Box sx={{ display: "flex", gap: "4px" }}>
        {camSlots.map((file, i) => (
          <CamThumb key={i} file={file} index={i} />
        ))}
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: "10px",
          pt: "8px",
          borderTop: "0.5px solid #e4e7ef",
        }}
      >
        <Typography sx={{ fontSize: "11px", color: "#94a3b8" }}>
          {record.fileNum ?? 0}/{record.expFileNum ?? 4} files
        </Typography>
        <StatusLabel value={status} />
      </Box>
    </Paper>
  );
};

const DatePill = ({ startDate, endDate, count }) => {
  const d1 = startDate?.slice(0, 10) ?? "—";
  const d2 = endDate?.slice(0, 10) ?? "—";
  const same = d1 === d2;
  return (
    <Box
      sx={{
        background: "#e8ecf7",
        borderRadius: "10px",
        border: "2px solid #082D6B",
        p: "12px 10px",
        textAlign: "center",
        minWidth: 96,
      }}
    >
      <Typography
        sx={{
          fontSize: "12px",
          fontWeight: 600,
          color: "#082D6B",
          lineHeight: 1.4,
        }}
      >
        {d1}
      </Typography>
      {!same && (
        <>
          <Typography
            sx={{ fontSize: "10px", color: "#4a5eaa", lineHeight: 1.2 }}
          >
            to
          </Typography>
          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 600,
              color: "#082D6B",
              lineHeight: 1.4,
            }}
          >
            {d2}
          </Typography>
        </>
      )}
      <Typography
        sx={{
          fontSize: "26px",
          fontWeight: 600,
          color: "#082D6B",
          mt: "4px",
          lineHeight: 1,
        }}
      >
        {count}
      </Typography>
      <Typography
        sx={{
          fontSize: "10px",
          color: "#4a5eaa",
          textTransform: "uppercase",
          letterSpacing: "0.4px",
          mt: "2px",
        }}
      >
        events
      </Typography>
    </Box>
  );
};

const FilterGroup = ({ label, children, width }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      gap: "4px",
      minWidth: width,
    }}
  >
    <Typography
      sx={{
        fontSize: "10px",
        fontWeight: 600,
        color: "#94a3b8",
        textTransform: "uppercase",
        letterSpacing: "0.4px",
      }}
    >
      {label}
    </Typography>
    {children}
  </Box>
);

// ─── Main Page ────────────────────────────────────────────────────────────────
const EventsPage = () => {
  const [mode, setMode] = useState("customer");
  const [alarmTypes, setAlarmTypes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [filters, setFilters] = useState({
    device: { deviceNames: "", deviceIds: "", devices: [] },
    customer: null,
    startDate: "2026-06-17T18:30",
    endDate: "2026-06-18T18:29",
    alertType: [],
    alertHandling: "",
    status: "1",
  });

  const [records, setRecords] = useState([]);
  const [fileMap, setFileMap] = useState({});
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searched, setSearched] = useState(false);

  // ── Event detail modal state ────────────────────────────────────────────────
  const [modalOpen, setModalOpen] = useState(false);
  const [modalRecord, setModalRecord] = useState(null);

  const handleCardClick = useCallback((record) => {
    setModalRecord(record);
    setModalOpen(true);
  }, []);

  // ── Select Device modal state ───────────────────────────────────────────────
  const [deviceModalOpen, setDeviceModalOpen] = useState(false);

  const lastParams = useRef(null);

  const handleFilterChange = (field, value) =>
    setFilters((prev) => ({ ...prev, [field]: value }));
  const handleDeviceModalConfirm = useCallback((deviceSelection) => {
    handleFilterChange("device", deviceSelection);
  }, []);

  const fmt = (dt) => dt.replace("T", " ") + ":00";

  const fetchAlarmTypes = async () => {
    try {
      const response = await getAlarmTypeList();
      if (response.code === 0) {
        setAlarmTypes(response.data || []);
      }
    } catch (error) {
      console.error("Alarm type fetch failed", error);
    }
  };

  const fetchUserBaseInfo = async () => {
    try {
      const response = await getbaseInfo();

      if (response.code === 0) {
        const customer = {
          label: response.data.customerName,
          value: response.data.customerId,
        };

        setCustomers([customer]);

        setFilters((prev) => ({
          ...prev,
          customer,
        }));
      }
    } catch (err) {
      console.error(err);
    }
  };
  useEffect(() => {
    fetchAlarmTypes();
    fetchUserBaseInfo();
  }, []);

  const buildParams = useCallback(
    (pageIndex = 1) => ({
      customerId: filters.customer?.value,
      deviceIds: filters.device.deviceIds || undefined,
      deviceNames: filters.device.deviceNames || undefined,
      queryType: mode === "customer" ? "Customer" : "Device",
      alarmType: filters.alertType.join(","),
      startTime: fmt(filters.startDate),
      endTime: fmt(filters.endDate),
      operates: filters.status ? Number(filters.status) : undefined,
      queryInvalidStatus:
        filters.alertHandling !== "" ? Number(filters.alertHandling) : 0,
      pageIndex,
    }),
    [filters, mode],
  );

  const loadPage = useCallback(async (pageIndex, params) => {
    setLoading(true);
    setError(null);
    try {
     const payload = {
  queryType: params.queryType,
  alarmType: params.alarmType || "",
  alarmIds: null,
  startTime: params.startTime,
  endTime: params.endTime,
  operates: params.operates,
  pageSize: PAGE_SIZE,
  pageIndex,
  queryInvalidStatus: params.queryInvalidStatus,
};

if (params.queryType === "Customer") {
  payload.customerId = params.customerId;
}

if (params.queryType === "Device") {
  payload.deviceIds = params.deviceIds;
  payload.deviceNames = params.deviceNames;
}

console.log(payload);

const response = await getAlarmPage(payload);

      if (response.code !== 0) throw new Error(response.msg || "API error");

      const result = response.data;
      const recs = result.records ?? [];
      setRecords(recs);
      setTotal(result.total ?? 0);

      if (recs.length === 0) {
        setFileMap({});
        return;
      }

      const alarmIds = recs.map((r) => r.alarmId).join(",");
      const deviceIds = [...new Set(recs.map((r) => r.deviceId))].join(",");
      const filesResponse = await getAlarmFiles({
        deviceIds,
        alarmType: params.alarmType || "",
        startTime: params.startTime,
        endTime: params.endTime,
        queryInvalidStatus: params.queryInvalidStatus,
        alarmIds,
      });

      if (filesResponse.code !== 0)
        throw new Error(filesResponse.msg || "API error");

      const filesArr = filesResponse.data || [];
      const map = {};
      for (const item of filesArr) {
        map[item.alarmId] = item.dailyAlarmFileList ?? [];
      }
      setFileMap(map);
    } catch (err) {
      console.error(err);
      setError(err.message ?? "Failed to fetch events. Please try again.");
      setRecords([]);
      setFileMap({});
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSearch = useCallback(async () => {
    const params = buildParams(1);
    lastParams.current = params;
    setPage(1);
    setSearched(true);
    await loadPage(1, params);
  }, [buildParams, loadPage]);

  const handlePageChange = useCallback(
    async (_, newPage) => {
      setPage(newPage);
      if (lastParams.current) {
        await loadPage(newPage, lastParams.current);
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    },
    [loadPage],
  );

  const handleReset = useCallback(() => {
    setFilters({
      device: { deviceNames: "", deviceIds: "", devices: [] },
      customer: customers[0] || null,
      startDate: "2026-06-17T18:30",
      endDate: "2026-06-18T18:29",
      alertType: [],
      alertHandling: "",
      status: "1",
    });
    setRecords([]);
    setFileMap({});
    setTotal(0);
    setPage(1);
    setSearched(false);
    setError(null);
    lastParams.current = null;
  }, []);

  const inputSx = { "& .MuiInputBase-root": { height: 34, fontSize: "13px" } };
  const selectSx = { height: 34, fontSize: "13px", borderRadius: "6px" };
  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <Box sx={{ backgroundColor: "#f0f2f7", minHeight: "100vh", pb: 10 }}>
      {/* ── Header + Filters ─────────────────────────────────────────── */}
      <Box
        sx={{
          background: "#fff",
          borderBottom: "0.5px solid #e4e7ef",
          px: { xs: 2, md: 3 },
          pt: 2,
          pb: 0,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            fontSize: "20px",
            color: "#1a2540",
            mb: "14px",
          }}
        >
          Events
        </Typography>

        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            gap: 1.5,
            flexWrap: "wrap",
            pb: "12px",
          }}
        >
          <FilterGroup label="Mode">
            <ToggleButtonGroup
              value={mode}
              exclusive
              onChange={(_, v) => v && setMode(v)}
              size="small"
              sx={{
                height: 34,
                "& .MuiToggleButton-root": {
                  fontSize: "12px",
                  px: 1.5,
                  textTransform: "none",
                  border: "0.5px solid #d1d5db",
                  color: "#64748b",
                  "&.Mui-selected": {
                    background: "#082D6B",
                    color: "#fff",
                    "&:hover": { background: "#0a3580" },
                  },
                },
              }}
            >
              <ToggleButton value="device">Device</ToggleButton>
              <ToggleButton value="customer">Customer</ToggleButton>
            </ToggleButtonGroup>
          </FilterGroup>

          {mode === "device" ? (
            <FilterGroup label="Device" width={220}>
              {/* "+" opens the Select Device modal (customer tree + device table) */}
              <Button
                onClick={() => setDeviceModalOpen(true)}
                startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                sx={{
                  height: 34,
                  width: 220,
                  justifyContent: "flex-start",
                  textTransform: "none",
                  fontSize: "13px",
                  color: filters.device.devices.length ? "#1a2540" : "#94a3b8",
                  background: "#fff",
                  border: "1px solid #c4cce0",
                  borderRadius: "6px",
                  px: 1.2,
                  "& .MuiButton-startIcon": {
                    background: "#082D6B",
                    color: "#fff",
                    borderRadius: "4px",
                    p: "3px",
                    mr: 1,
                  },
                  "&:hover": { borderColor: "#082D6B", background: "#fff" },
                }}
              >
                {filters.device.devices.length
                  ? filters.device.deviceNames
                  : "Please Select Device"}
              </Button>
            </FilterGroup>
          ) : (
            <FilterGroup label="Customer" width={200}>
              <Autocomplete
                size="small"
                options={customers}
                value={filters.customer}
                onChange={(_, v) => handleFilterChange("customer", v)}
                sx={{ width: 200 }}
                renderInput={(p) => (
                  <TextField
                    {...p}
                    placeholder="Select customer"
                    sx={inputSx}
                  />
                )}
              />
            </FilterGroup>
          )}

          <FilterGroup label="Date range">
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <TextField
                type="datetime-local"
                size="small"
                value={filters.startDate}
                onChange={(e) =>
                  handleFilterChange("startDate", e.target.value)
                }
                InputLabelProps={{ shrink: true }}
                sx={{ width: 158, ...inputSx }}
              />
              <Typography sx={{ color: "#94a3b8", fontSize: 14 }}>–</Typography>
              <TextField
                type="datetime-local"
                size="small"
                value={filters.endDate}
                onChange={(e) => handleFilterChange("endDate", e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 158, ...inputSx }}
              />
            </Stack>
          </FilterGroup>

          <FilterGroup label="Alert type" width={155}>
            <Autocomplete
              multiple
              size="small"
              options={alarmTypes}
              disableCloseOnSelect
              value={alarmTypes.filter((item) =>
                filters.alertType.includes(item.formatKey),
              )}
              onChange={(_, value) =>
                handleFilterChange(
                  "alertType",
                  value.map((item) => item.formatKey),
                )
              }
              getOptionLabel={(option) => option.formatValue}
              renderTags={() => null}
              sx={{
                width: 155,
                "& .MuiOutlinedInput-root": {
                  height: 34,
                  fontSize: "13px",
                  alignItems: "center",
                },
                "& .MuiAutocomplete-inputRoot": {
                  paddingTop: "0 !important",
                  paddingBottom: "0 !important",
                },
                "& .MuiAutocomplete-input": {
                  padding: "0 !important",
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={
                    filters.alertType.length
                      ? `${filters.alertType.length} selected`
                      : "All types"
                  }
                  InputProps={{
                    ...params.InputProps,
                  }}
                  sx={{
                    "& .MuiInputBase-root": {
                      height: 34,
                      fontSize: "13px",
                    },
                    "& .MuiInputBase-input::placeholder": {
                      opacity: 1,
                      color: "#94a3b8",
                    },
                  }}
                />
              )}
            />
          </FilterGroup>

          <FilterGroup label="Handling" width={115}>
            <FormControl size="small" sx={{ width: 115 }}>
              <Select
                value={filters.alertHandling}
                onChange={(e) =>
                  handleFilterChange("alertHandling", e.target.value)
                }
                displayEmpty
                sx={selectSx}
              >
                <MenuItem value="">
                  <em style={{ color: "#94a3b8" }}>All</em>
                </MenuItem>
                <MenuItem value="0">Valid</MenuItem>
                <MenuItem value="1">Invalid</MenuItem>
              </Select>
            </FormControl>
          </FilterGroup>

          <FilterGroup label="Status" width={125}>
            <FormControl size="small" sx={{ width: 125 }}>
              <Select
                value={filters.status}
                onChange={(e) => handleFilterChange("status", e.target.value)}
                displayEmpty
                sx={selectSx}
              >
                {/* <MenuItem value=""><em style={{ color: "#94a3b8" }}>All</em></MenuItem> */}
                <MenuItem value="1">Completed</MenuItem>
                <MenuItem value="2">Pending</MenuItem>
                <MenuItem value="3">Processing</MenuItem>
              </Select>
            </FormControl>
          </FilterGroup>

          <Stack
            direction="row"
            spacing={1}
            sx={{ ml: "auto", alignItems: "flex-end" }}
          >
            <Button
              variant="outlined"
              startIcon={<UploadFileIcon />}
              size="small"
              sx={{
                height: 34,
                textTransform: "none",
                fontSize: "13px",
                borderColor: "#c5d3f5",
                color: "#082D6B",
                background: "#f0f4ff",
                borderRadius: "6px",
                "&:hover": { background: "#e4ecff", borderColor: "#082D6B" },
              }}
            >
              File not uploaded
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={handleSearch}
              disabled={loading}
              startIcon={
                loading ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <SearchIcon />
                )
              }
              sx={{
                height: 34,
                textTransform: "none",
                fontSize: "13px",
                background: "#082D6B",
                borderRadius: "6px",
                px: 2.5,
                "&:hover": { background: "#0a3580" },
              }}
            >
              {loading ? "Searching…" : "Search"}
            </Button>
            <Button
              variant="outlined"
              startIcon={<RestartAltIcon />}
              onClick={handleReset}
              size="small"
              sx={{
                height: 34,
                textTransform: "none",
                fontSize: "13px",
                borderColor: "#e4e7ef",
                color: "#64748b",
                borderRadius: "6px",
                "&:hover": { borderColor: "#082D6B", color: "#082D6B" },
              }}
            >
              Reset
            </Button>
          </Stack>
        </Box>
      </Box>

      {/* ── Content ──────────────────────────────────────────────────── */}
      <Box sx={{ p: { xs: 2, md: "20px 24px" } }}>
        {error && (
          <Paper
            elevation={0}
            sx={{
              mb: 2,
              p: 2,
              borderRadius: "8px",
              background: "#fce8e6",
              border: "0.5px solid #f5c6c2",
            }}
          >
            <Typography sx={{ fontSize: "13px", color: "#b91c1c" }}>
              ⚠ {error}
            </Typography>
          </Paper>
        )}

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
            <CircularProgress sx={{ color: "#082D6B" }} />
          </Box>
        )}

        {!loading && searched && records.length > 0 && (
          <Box sx={{ display: "flex", gap: 2, height: "calc(100vh - 250px)" }}>
            {/* Left Date Pill */}
            <Box
              sx={{
                flexShrink: 0,
                position: "sticky",
                top: 0,
                alignSelf: "flex-start",
              }}
            >
              <DatePill
                startDate={filters.startDate}
                endDate={filters.endDate}
                count={total}
              />
            </Box>

            {/* Right Side */}
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
              }}
            >
              {/* Scrollable Cards */}
              <Box sx={{ flex: 1, overflowY: "auto", pr: 1 }}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, 1fr)",
                    gap: "12px",
                  }}
                >
                  {records.map((rec) => (
                    <EventCard
                      key={rec.alarmId}
                      record={rec}
                      fileMap={fileMap}
                      onClick={() => handleCardClick(rec)} // ← wire click
                    />
                  ))}
                </Box>
              </Box>

              {/* Pagination */}
              <Box
                sx={{
                  mt: 2,
                  pt: 2,
                  borderTop: "1px solid #e4e7ef",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 2,
                  background: "#f0f2f7",
                }}
              >
                <Typography sx={{ fontSize: "13px", color: "#64748b" }}>
                  Showing {(page - 1) * PAGE_SIZE + 1}–
                  {Math.min(page * PAGE_SIZE, total)} of {total}
                </Typography>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                  shape="rounded"
                  size="small"
                  sx={{
                    "& .MuiPaginationItem-root": { fontSize: "13px" },
                    "& .Mui-selected": {
                      background: "#082D6B !important",
                      color: "#fff",
                    },
                  }}
                />
              </Box>
            </Box>
          </Box>
        )}

        {!loading && searched && records.length === 0 && !error && (
          <Paper
            elevation={0}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 14,
              borderRadius: "12px",
              border: "0.5px solid #e4e7ef",
            }}
          >
            <SearchIcon sx={{ fontSize: 56, color: "#cbd5e1", mb: 2 }} />
            <Typography sx={{ fontSize: "15px", color: "#94a3b8" }}>
              No events found for selected filters
            </Typography>
          </Paper>
        )}

        {!loading && !searched && (
          <Paper
            elevation={0}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 14,
              borderRadius: "12px",
              border: "0.5px solid #e4e7ef",
            }}
          >
            <SearchIcon sx={{ fontSize: 56, color: "#cbd5e1", mb: 2 }} />
            <Typography sx={{ fontSize: "15px", color: "#94a3b8" }}>
              Apply filters and click Search
            </Typography>
          </Paper>
        )}
      </Box>

      {/* ── Event Detail Modal ────────────────────────────────────────── */}
      <EventDetailModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        records={records}
        fileMap={fileMap}
        initialRecord={modalRecord}
      />

      {/* ── Select Device Modal ───────────────────────────────────────── */}
      <SelectDeviceModal
        open={deviceModalOpen}
        onClose={() => setDeviceModalOpen(false)}
        onConfirm={handleDeviceModalConfirm}
        initialSelectedDevices={filters.device.devices}
      />
    </Box>
  );
};

export default EventsPage;
